/**
 * Data Governance & PII Sanitization
 *
 * All analytics, logs, and exports pass through sanitizeProps to ensure
 * no personally identifiable information (PII) leaks outside the system.
 *
 * INVARIANTS:
 * - Never emit raw email, phone, name, or tokens
 * - Hash PII with SHA256, keeping only last 4 characters visible
 * - Normal props pass through unchanged
 */

/**
 * PII field patterns to sanitize
 */
const PII_KEYS =
  /^(email|phone|name|token|api_?key|secret|password|ssn|credit_?card|address|zip|postal)$/i;

/**
 * Regex patterns to detect PII values
 */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_PATTERN = /^\+?[\d\s().-]{10,}$/;
const TOKEN_PATTERN = /^(api_|sk_|pk_|tok_|key_)/i;

/**
 * Hash a sensitive value using SHA256
 * Returns: "hash_<first8>_<last4visible>"
 */
export async function hashValue(value: string): Promise<string> {
  if (!value || typeof value !== 'string') return '';

  try {
    // Use Web Crypto API (available in browsers and Deno)
    const encoder = new TextEncoder();
    const data = encoder.encode(value);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');

    // Keep last 4 chars visible for debugging, rest is hash
    const last4 = value.slice(-4);
    const hashPrefix = hashHex.substring(0, 8);

    return `hash_${hashPrefix}_${last4}`;
  } catch (error) {
    // Fallback if crypto not available
    return `hash_redacted_${value.slice(-4)}`;
  }
}

/**
 * Synchronous hash for environments without crypto.subtle
 * Uses a simple hash algorithm (not cryptographically secure)
 */
export function hashValueSync(value: string): string {
  if (!value || typeof value !== 'string') return '';

  // Simple hash (not secure, but sufficient for PII redaction)
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    const char = value.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }

  const hashHex = Math.abs(hash).toString(16).padStart(8, '0');
  const last4 = value.slice(-4);

  return `hash_${hashHex}_${last4}`;
}

/**
 * Check if a key should be sanitized
 */
function shouldSanitizeKey(key: string): boolean {
  return PII_KEYS.test(key);
}

/**
 * Check if a value looks like PII
 */
function looksLikePII(value: any): boolean {
  if (typeof value !== 'string') return false;

  return EMAIL_PATTERN.test(value) || PHONE_PATTERN.test(value) || TOKEN_PATTERN.test(value);
}

/**
 * Sanitize a single value
 */
function sanitizeValue(value: any, key?: string): any {
  // Null/undefined pass through
  if (value == null) return value;

  // Arrays: sanitize each element
  if (Array.isArray(value)) {
    return value.map((v) => sanitizeValue(v));
  }

  // Objects: recursively sanitize
  if (typeof value === 'object') {
    return sanitizeProps(value);
  }

  // Strings: check if PII
  if (typeof value === 'string') {
    // Check if key indicates PII
    if (key && shouldSanitizeKey(key)) {
      return hashValueSync(value);
    }

    // Check if value looks like PII
    if (looksLikePII(value)) {
      return hashValueSync(value);
    }
  }

  // All other primitives pass through
  return value;
}

/**
 * Sanitize an object's properties
 *
 * @param obj - Object with potentially sensitive data
 * @returns New object with PII sanitized
 */
export function sanitizeProps(obj: Record<string, any>): Record<string, any> {
  if (!obj || typeof obj !== 'object') return obj;

  const sanitized: Record<string, any> = {};

  for (const [key, value] of Object.entries(obj)) {
    sanitized[key] = sanitizeValue(value, key);
  }

  return sanitized;
}

/**
 * Sanitize an array of objects (for exports/logs)
 */
export function sanitizeArray<T extends Record<string, any>>(arr: T[]): T[] {
  return arr.map((item) => sanitizeProps(item) as T);
}

/**
 * Get sanitization rules for documentation
 */
export function getSanitizationRules() {
  return {
    keys: [
      'email',
      'phone',
      'name',
      'token',
      'api_key',
      'secret',
      'password',
      'ssn',
      'credit_card',
      'address',
      'zip',
      'postal',
    ],
    patterns: [
      { type: 'email', pattern: 'x@y.z format' },
      { type: 'phone', pattern: '10+ digits with optional +/-/()' },
      { type: 'token', pattern: 'starts with api_, sk_, pk_, tok_, key_' },
    ],
    strategy: 'SHA256 hash with last 4 characters visible',
    output: 'hash_<8chars>_<last4>',
  };
}

/**
 * Demo sanitization for testing/documentation
 */
export function demoSanitization() {
  const samples = {
    email: 'user@example.com',
    phone: '+1-555-444-3333',
    token: 'api_key_1234567890',
    name: 'John Doe',
    normalProp: 'some-value',
    count: 42,
    enabled: true,
  };

  return {
    original: samples,
    sanitized: sanitizeProps(samples),
  };
}
